/**
 * ViewsRepo — typed repository over {@link DocStorePort} for
 * `creations/{id}/viewShards/{0..9}`. Views are counted with a SHARDED counter
 * to avoid write contention on a single hot doc: the portal increments a random
 * shard per view, and a scheduled function sums the shards into
 * `Creation.viewsCount`.
 *
 * {@link incrementShard} uses the doc store's `increment` directive on a merge
 * (create-or-add), so a shard that doesn't exist yet starts from 0.
 * {@link readTotal} sums all shards for a live total (the function's rollup uses
 * the same sum).
 */
import { type DocStorePort } from '@djgg/core';
import { VIEW_SHARD_COUNT, type ViewShard } from '../types.js';
import { viewShardDoc, viewShardsCollection, viewerDoc, creationDoc } from '../paths.js';

export class ViewsRepo {
  constructor(
    private readonly store: DocStorePort,
    /**
     * Shard picker (0..VIEW_SHARD_COUNT-1). Defaults to Math.random-based; a
     * test injects a deterministic picker. NOT sim code, so Math.random is
     * acceptable here — this is data-plane infra, not the deterministic world.
     */
    private readonly pickShard: () => number = () => Math.floor(Math.random() * VIEW_SHARD_COUNT),
  ) {}

  /**
   * Record a view by incrementing a random shard by 1. Returns the shard index
   * that was bumped. Uses mergeDoc + increment so a fresh shard starts at 0.
   */
  async incrementShard(creationId: string): Promise<number> {
    const raw = this.pickShard();
    // clamp defensively so an out-of-range injected picker can't write a stray shard
    const shard = Math.min(VIEW_SHARD_COUNT - 1, Math.max(0, Math.floor(raw)));
    await this.store.mergeDoc<ViewShard & Record<string, unknown>>(
      viewShardDoc(creationId, shard),
      { count: this.store.increment(1) as unknown as number },
    );
    return shard;
  }

  /** Sum every shard for the live view total. */
  async readTotal(creationId: string): Promise<number> {
    const shards = await this.store.query<ViewShard>(viewShardsCollection(creationId));
    return shards.reduce((sum, s) => sum + (typeof s.count === 'number' ? s.count : 0), 0);
  }

  /**
   * Record a DISTINCT viewer: if `viewers/{uid}` is absent, create the marker
   * and increment `Creation.distinctViewers`. Returns true when newly recorded.
   * Does NOT touch `updatedAt` (a view must not resurface a game in trending).
   * Read-then-write (no txn primitive) — a rare concurrent first-view could
   * double-count; benign for a popularity metric.
   */
  async recordDistinctViewer(creationId: string, uid: string): Promise<boolean> {
    if (await this.store.getDoc(viewerDoc(creationId, uid))) return false;
    await this.store.setDoc(viewerDoc(creationId, uid), { at: this.store.serverTimestamp() });
    await this.store.update(creationDoc(creationId), {
      distinctViewers: this.store.increment(1),
    });
    return true;
  }

  /**
   * Record an engaged play: increment the counter (`Creation.playsCount`) and
   * stamp `lastPlayedAt` in the SAME update — the count and its recency are two
   * views of one event, so they are written together rather than racing as two
   * updates (and the gallery's live subscription sees one row change, not two).
   * Does NOT touch `updatedAt`: a play must not resurface a game in trending.
   */
  async incrementPlays(creationId: string, at: number): Promise<void> {
    await this.store.update(creationDoc(creationId), {
      playsCount: this.store.increment(1),
      lastPlayedAt: at,
    });
  }
}
